import Component from '@glimmer/component';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { task, rawTimeout, timeout } from 'ember-concurrency';
import noUiSlider from 'nouislider';
import { add } from 'date-fns';
import { registerDestructor } from '@ember/destroyable';

export default class PositionControlComponent extends Component {
  @service stereo;
  @service feeds;
  @service intl;
  @service clock;

  // The pre-play range uses the airing's scheduled length; once the sound loads
  // we correct it to the sound's real duration exactly once.
  _rangeFromSound = false;

  constructor() {
    super(...arguments);
    registerDestructor(this, () => {
      // destroy() removes the slider's own event listeners, so there's no need
      // to off() them first.
      this.slider?.destroy();
    });
  }

  get startsAt() {
    return (
      this.args.feed?.airing?.actualStartsAt || this.args.feed?.airing?.startsAt
    );
  }

  // Both from the feed, never the sound: isRewindable is where the station's
  // say-so is read, and a relay advertising byte ranges makes the sound claim
  // to be seekable whether or not the station allows rewinding live.
  get isLiveAiring() {
    return !!(this.args.feed?.isLiveEdge && this.args.feed?.isRewindable);
  }

  get soundIsSeekable() {
    // A live broadcast is pinned to the live edge, never scrubbed in place.
    if (this.args.feed?.isLiveEdge) return false;
    return this.loadedSound?.isSeekable;
  }

  positionToTime = (position) => {
    if (this.startsAt && position) {
      return add(new Date(this.startsAt), {
        seconds: (position || 0) / 1000,
      });
    } else if (this.startsAt) {
      return this.startsAt;
    } else {
      return undefined;
    }
  };

  get airingDuration() {
    let endsAt = this.args.feed?.airing?.endsAt;
    if (!this.startsAt || !endsAt) return 0;
    return new Date(endsAt).getTime() - new Date(this.startsAt).getTime();
  }

  // noUiSlider requires finite numeric range bounds. A live stream's sound
  // reports an Infinite (or NaN) duration, which makes create()/updateOptions()
  // throw "'range' value isn't numeric". Coerce to a usable finite value, or
  // null when there's nothing valid to use.
  finiteMax(value) {
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  get elapsedDuration() {
    if (!this.startsAt) return 0;
    return this.clock.date.getTime() - new Date(this.startsAt).getTime();
  }

  // Restartable so a feed switch (didInsert re-perform) cancels the in-flight
  // initialize instead of double-creating the slider, and re-performing cancels
  // the previous playhead loop rather than stacking another.
  initializeSliderTask = task({ restartable: true }, async () => {
    let min = 0;
    let max;
    this._rangeFromSound = false;

    if (this.isLiveAiring) {
      while (this.airingDuration <= 0) {
        await rawTimeout(100);
      }
      max = this.airingDuration;

      let elapsed = this.elapsedDuration;
      let startPosition = this.soundIsSeekable
        ? this.loadedSound.position
        : elapsed;

      this.slider = noUiSlider.create(this.sliderElement, {
        connect: [true, true, false],
        keyboardSupport: true,
        keyboardDefaultStep: 1000,
        tooltips: [
          {
            to: (value) =>
              this.intl.formatTime(this.positionToTime(value), {
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                timeZone: this.args.timeZone,
                locale: this.args.locale,
              }),
          },
          false,
        ],
        range: { min: [min, 1000], max: [max, 1000] },
        start: [startPosition, elapsed],
        margin: 0,
        cssPrefix: 'dubbletrack-slider-',
        handleAttributes: [
          {},
          { 'aria-hidden': 'true' },
        ],
      });

      let upperHandle = this.sliderElement.querySelector('[data-handle="1"]');
      if (upperHandle) {
        upperHandle.style.display = 'none';
        upperHandle.style.pointerEvents = 'none';
      }
    } else {
      // Render immediately from the airing's known (scheduled) duration — there
      // is no loaded sound until play, so don't block on transportIsSeekable.
      // updatePlayheadTask corrects the range to the sound's real duration once
      // it loads. Fall back to waiting only when no duration is known at all.
      // Only adopt the sound's duration when it's finite — a live stream reports
      // Infinity, which noUiSlider rejects.
      let soundMax = this.transportIsSeekable
        ? this.finiteMax(this.transportDuration)
        : null;
      max = soundMax ?? this.finiteMax(this.airingDuration);
      if (!max) {
        while (!this.finiteMax(this.transportDuration)) {
          await rawTimeout(10);
        }
        max = this.transportDuration;
      }

      this.slider = noUiSlider.create(this.sliderElement, {
        connect: [true, false],
        keyboardSupport: true,
        keyboardDefaultStep: 1000,
        tooltips: [
          {
            to: (value) =>
              this.intl.formatTime(this.positionToTime(value), {
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                timeZone: this.args.timeZone,
                locale: this.args.locale,
              }),
          },
        ],
        range: { min: [min, 1000], max: [max, 1000] },
        padding: [10],
        start: [this.transportPosition ?? 0],
        cssPrefix: 'dubbletrack-slider-',
      });
    }

    // Respect the station's playback/scrubbing tolerance: when disabled, the
    // bar still tracks the playhead (setHandle works on a disabled slider) but
    // listeners can't drag or tap to seek.
    if (this.args.feed?.canScrub === false) {
      this.slider.disable();
    } else {
      this.slider.enable();
      this.slider.on('change', this.onSliderUpdate.bind(this));
      this.slider.on('slide', this.onSliderUpdate.bind(this));
      this.slider.on('drag', this.onSliderUpdate.bind(this));
    }
    this.updatePlayheadTask.perform();
  });

  goLive() {
    this.feeds.goLive();
  }

  onSliderUpdate(values, handle) {
    if (this.isLiveAiring) {
      if (handle === 1) {
        this.slider.setHandle(1, this.elapsedDuration, false);
        return;
      }

      let rawPosition = parseFloat(values[0]);
      if (rawPosition >= this.elapsedDuration) {
        this.goLive();
        return;
      }

      this.slider.setHandle(0, rawPosition, false);

      if (this.soundIsSeekable) {
        this.loadedSound.position = rawPosition;
      } else {
        this.feeds.scrubToFeed(this.args.feed.airing, rawPosition);
      }
      return;
    }

    let position = values[handle];

    if (position) {
      this.args.feed?.seek(position);
    }

    let payload = {
      handle: handle,
      time: this.positionToTime(values[handle]),
      position: values[handle],
    };

    if ('onUpdate' in this.args) {
      this.args.onUpdate(handle, payload);
    }
    if ('onUpdateStart' in this.args && handle == 0) {
      this.args.onUpdateStart(payload);
    }
    if ('onUpdateEnd' in this.args && handle == 1) {
      this.args.onUpdateEnd(payload);
    }
  }

  updatePlayheadTask = task({ restartable: true }, async () => {
    while (true) {
      if (this.slider) {
        if (this.isLiveAiring) {
          let elapsed = this.elapsedDuration;
          this.slider.setHandle(1, elapsed, false);

          // Don't fight a pending rewind: while the feed loads a new position,
          // leave the handle where the drag put it.
          if (!this.feeds.playFeedTask.isRunning) {
            this.slider.setHandle(0, elapsed, false);
          }
        } else if (this.transportIsPlaying) {
          // Only resize the range to the sound's duration when it's finite — a
          // live stream reports Infinity, which noUiSlider rejects ("'range'
          // value isn't numeric"). The scheduled-length range from create()
          // stays in place otherwise.
          let nextMax = null;
          if (this.args.feed?.isLive) {
            nextMax = this.finiteMax(this.transportDuration);
          } else if (this.transportIsSeekable && !this._rangeFromSound) {
            // Correct the pre-play (scheduled-length) range to the sound's real
            // duration, once, now that it's loaded.
            nextMax = this.finiteMax(this.transportDuration);
            if (nextMax) this._rangeFromSound = true;
          }
          if (nextMax) {
            this.slider.updateOptions({
              range: {
                min: [0, 1000],
                max: [nextMax, 1000],
              },
            });
          }
          if (Number.isFinite(this.transportPosition)) {
            this.slider.setHandle(0, this.transportPosition, false);
          }
        }
      }
      await timeout(1000);
    }
  });

  get loadedSound() {
    return this.args.feed?.loadedSound;
  }

  // Transport contract — follows the remote device while casting (the local
  // sound is paused then), the local sound otherwise. See Feed#transport*.
  get transportPosition() {
    return this.args.feed?.transportPosition;
  }

  get transportDuration() {
    return this.args.feed?.transportDuration;
  }

  get transportIsPlaying() {
    return this.args.feed?.transportIsPlaying;
  }

  get transportIsSeekable() {
    return this.args.feed?.transportIsSeekable;
  }

  @action
  async didInsert(element) {
    this.sliderElement = element;
    if (this.slider) {
      this.slider.destroy();
    }
    this.initializeSliderTask.perform();
  }

  // Preview where a tap would seek to: track the cursor across the slider and
  // expose its position (0–100%) as a CSS var that lights up the bar up to it.
  @action
  onScrubHover(event) {
    // No seek preview when the station disallows scrubbing.
    if (this.args.feed?.canScrub === false) return;

    let element = event.currentTarget;
    let { left, width } = element.getBoundingClientRect();
    if (!width) return;

    let percent = Math.min(1, Math.max(0, (event.clientX - left) / width));
    element.style.setProperty('--scrub-hover', `${percent * 100}%`);
  }

  @action
  onScrubHoverLeave(event) {
    event.currentTarget.style.removeProperty('--scrub-hover');
  }
}
